// MCP-based database service for development
// This simulates what would be a Cloudflare Worker API in production

const DATABASE_ID = 'bd73b18c-544d-4d42-9558-a6181850498e';

class MCPDatabaseService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Simulate MCP query - in production this would go through a Worker API
  async query(sql, params = []) {
    try {
      // Since we can't directly call MCP from the browser, we'll simulate the response
      // In a real deployment, this would be an API call to a Cloudflare Worker
      console.log('MCP Query (simulated):', sql, params);
      
      // Return mock data based on common queries
      if (sql.includes('SELECT') && sql.includes('events_cache')) {
        return await this.getMockEvents();
      }
      
      return { results: [], success: true };
    } catch (error) {
      console.error('Database query error:', error);
      throw error;
    }
  }

  // Mock events that match our database schema
  async getMockEvents() {
    return {
      results: [
        {
          id: '1',
          title: 'Jazz Night at Dimitriou\'s Jazz Alley',
          date: '2025-07-18',
          time: '8:00 PM',
          location: 'Dimitriou\'s Jazz Alley, Seattle',
          description: 'An evening of smooth jazz featuring local and touring musicians in Seattle\'s premier jazz venue.',
          category: 'Music',
          source: 'SeattleMet',
          source_url: 'https://www.seattlemet.com/arts-and-culture/jazz-night-dimitrious-jazz-alley',
          event_type: 'single',
          start_date: null,
          end_date: null
        },
        {
          id: '2',
          title: 'Pike Place Market Food Tour',
          date: '2025-07-19',
          time: '11:00 AM',
          location: 'Pike Place Market, Seattle',
          description: 'Guided culinary tour through Pike Place Market featuring tastings from local vendors.',
          category: 'Food',
          source: 'SeattleMet',
          source_url: 'https://www.seattlemet.com/eat-and-drink/pike-place-market-food-tour',
          event_type: 'recurring',
          start_date: null,
          end_date: null
        },
        {
          id: '15',
          title: 'Seattle International Film Festival',
          date: '2025-07-24',
          time: 'Various times',
          location: 'Multiple venues across Seattle',
          description: 'Annual celebration of international cinema with screenings, premieres, and filmmaker talks across the city.',
          category: 'Film',
          source: 'SeattleMet',
          source_url: 'https://www.seattlemet.com/arts-and-culture/siff-2025',
          event_type: 'multiDay',
          start_date: '2025-07-24',
          end_date: '2025-08-02'
        },
        {
          id: '3',
          title: 'Capitol Hill Art Walk',
          date: '2025-07-17',
          time: '6:00 PM',
          location: 'Capitol Hill, Seattle',
          description: 'Monthly art walk featuring galleries, studios, and pop-up exhibitions throughout Capitol Hill.',
          category: 'Arts',
          source: 'EverOut',
          source_url: 'https://everout.com/seattle/events/capitol-hill-art-walk-july/e150234/',
          event_type: 'recurring',
          start_date: null,
          end_date: null
        },
        {
          id: '4',
          title: 'Indie Film Screening: Local Stories',
          date: '2025-07-20',
          time: '7:30 PM',
          location: 'SIFF Cinema Uptown, Seattle',
          description: 'Special screening of locally-produced independent films with director Q&A.',
          category: 'Film',
          source: 'EverOut',
          source_url: 'https://everout.com/seattle/events/indie-film-screening-local-stories/e150456/',
          event_type: 'single',
          start_date: null,
          end_date: null
        },
        {
          id: '5',
          title: 'Waterfront Summer Concert Series',
          date: '2025-07-21',
          time: '7:00 PM',
          location: 'Waterfront Park, Seattle',
          description: 'Free outdoor concert featuring Pacific Northwest bands with views of Elliott Bay.',
          category: 'Music',
          source: 'VisitSeattle',
          source_url: 'https://visitseattle.org/events/waterfront-summer-concert-series/',
          event_type: 'recurring',
          start_date: null,
          end_date: null
        },
        {
          id: '6',
          title: 'Space Needle Night Market',
          date: '2025-07-22',
          time: '5:00 PM',
          location: 'Seattle Center, Seattle',
          description: 'Evening market with local artisans, food trucks, and live entertainment at Seattle Center.',
          category: 'Market',
          source: 'VisitSeattle',
          source_url: 'https://visitseattle.org/events/space-needle-night-market/',
          event_type: 'single',
          start_date: null,
          end_date: null
        },
        {
          id: '16',
          title: 'Chihuly Garden and Glass: Summer Exhibition',
          date: '2025-06-01',
          time: '9:00 AM - 7:00 PM',
          location: 'Chihuly Garden and Glass, Seattle Center',
          description: 'Extended summer exhibition featuring new installations and extended evening hours with special lighting.',
          category: 'Arts',
          source: 'VisitSeattle',
          source_url: 'https://visitseattle.org/events/chihuly-summer-exhibition/',
          event_type: 'ongoing',
          start_date: '2025-06-01',
          end_date: '2025-09-30'
        },
        {
          id: '17',
          title: 'Capitol Hill Monthly Art Walk',
          date: '2025-07-17',
          time: '6:00 PM',
          location: 'Capitol Hill, Seattle',
          description: 'Monthly gallery walk featuring local artists and studios throughout the Capitol Hill neighborhood.',
          category: 'Arts',
          source: 'VisitSeattle',
          source_url: 'https://visitseattle.org/events/capitol-hill-art-walk/',
          event_type: 'single',
          start_date: null,
          end_date: null
        },
        {
          id: '7',
          title: 'Fremont Sunday Market',
          date: '2025-07-20',
          time: '10:00 AM',
          location: 'Fremont, Seattle',
          description: 'Weekly farmers market and artisan fair in the heart of quirky Fremont neighborhood.',
          category: 'Market',
          source: 'SeattleMag',
          source_url: 'https://seattlemag.com/calendar/fremont-sunday-market-july',
          event_type: 'recurring',
          start_date: null,
          end_date: null
        },
        {
          id: '8',
          title: 'Ballard Beer & Food Festival',
          date: '2025-07-19',
          time: '2:00 PM - 10:00 PM',
          location: 'Ballard Commons Park, Seattle',
          description: 'Two-day celebration of local breweries and restaurants with tastings, live music, and food competitions.',
          category: 'Food',
          source: 'SeattleMag',
          source_url: 'https://seattlemag.com/calendar/ballard-beer-food-festival',
          event_type: 'multiDay',
          start_date: '2025-07-19',
          end_date: '2025-07-20'
        },
        {
          id: '18',
          title: 'Seattle Waterfront Concert Series',
          date: '2025-07-21',
          time: '7:30 PM',
          location: 'Waterfront Park, Seattle',
          description: 'Free outdoor summer concert series featuring Pacific Northwest musicians with stunning Elliott Bay views.',
          category: 'Music',
          source: 'SeattleMag',
          source_url: 'https://seattlemag.com/calendar/waterfront-concert-series',
          event_type: 'single',
          start_date: null,
          end_date: null
        },
        {
          id: '9',
          title: 'Immersive Van Gogh Experience',
          date: '2025-07-23',
          time: '6:00 PM',
          location: 'Lighthouse ArtSpace, Seattle',
          description: 'Digital art exhibition featuring Van Gogh\'s masterpieces in an immersive environment.',
          category: 'Arts',
          source: 'Fever',
          source_url: 'https://feverup.com/en/seattle/plans/immersive-van-gogh-seattle',
          event_type: 'single',
          start_date: null,
          end_date: null
        },
        {
          id: '10',
          title: 'Rooftop Yoga with City Views',
          date: '2025-07-18',
          time: '6:30 AM',
          location: 'Downtown Seattle Rooftop',
          description: 'Morning yoga session on a downtown rooftop with panoramic views of the city and mountains.',
          category: 'Wellness',
          source: 'Fever',
          source_url: 'https://feverup.com/en/seattle/plans/rooftop-yoga-city-views',
          event_type: 'recurring',
          start_date: null,
          end_date: null
        },
        {
          id: '11',
          title: 'Pike Place Market Culinary Tour',
          date: '2025-07-19',
          time: '11:30 AM',
          location: 'Pike Place Market, Seattle',
          description: 'Guided food tasting tour through historic Pike Place Market with local vendor tastings.',
          category: 'Food',
          source: 'Fever',
          source_url: 'https://feverup.com/en/seattle/plans/pike-place-market-food-tour',
          event_type: 'single',
          start_date: null,
          end_date: null
        }
      ],
      success: true
    };
  }

  // Transform database event to app format (same as database service)
  transformDatabaseEvent(dbEvent) {
    const event = {
      id: dbEvent.id,
      title: dbEvent.title,
      date: dbEvent.date,
      time: dbEvent.time,
      location: dbEvent.location,
      description: dbEvent.description,
      category: dbEvent.category,
      source: dbEvent.source,
      url: dbEvent.source_url,
      eventType: dbEvent.event_type || 'single',
    };

    // Add optional fields
    if (dbEvent.start_date) event.startDate = dbEvent.start_date;
    if (dbEvent.end_date) event.endDate = dbEvent.end_date;

    // Handle different event types
    if (event.eventType === 'recurring') {
      event.recurrence = this.parseRecurrenceFromEvent(event);
    }

    if (event.eventType === 'multiDay') {
      event.dailySchedule = this.parseDailyScheduleFromEvent(event);
    }

    return event;
  }

  // Parse recurrence information (simplified for now)
  parseRecurrenceFromEvent(event) {
    // Hardcoded recurrence data for demo - in real app this would be stored in DB
    const recurrenceMap = {
      '2': { frequency: 'daily', daysOfWeek: ['Tuesday', 'Thursday', 'Saturday'], endDate: '2025-08-31' },
      '3': { frequency: 'monthly', dayOfMonth: 'second Thursday', endDate: '2025-12-31' },
      '5': { frequency: 'weekly', daysOfWeek: ['Monday'], endDate: '2025-09-01' },
      '7': { frequency: 'weekly', daysOfWeek: ['Sunday'], endDate: '2025-10-31' },
      '10': { frequency: 'weekly', daysOfWeek: ['Tuesday', 'Friday'], endDate: '2025-12-31' }
    };
    
    return recurrenceMap[event.id] || {
      frequency: 'weekly',
      daysOfWeek: [new Date(event.date).toLocaleDateString('en-US', { weekday: 'long' })],
      endDate: '2025-12-31'
    };
  }

  // Parse daily schedule (simplified for now)
  parseDailyScheduleFromEvent(event) {
    if (!event.startDate || !event.endDate) return [];
    
    // Hardcoded schedules for demo
    if (event.id === '15') {
      return [
        { date: '2025-07-24', times: ['7:00 PM', '9:30 PM'], focus: 'Opening Night Gala' },
        { date: '2025-07-25', times: ['2:00 PM', '5:00 PM', '8:00 PM'], focus: 'International Shorts' },
        { date: '2025-07-26', times: ['1:00 PM', '4:00 PM', '7:00 PM'], focus: 'Documentary Day' }
      ];
    }
    
    if (event.id === '8') {
      return [
        { date: '2025-07-19', times: ['2:00 PM - 10:00 PM'], focus: 'Brewery Showcase & Live Music' },
        { date: '2025-07-20', times: ['12:00 PM - 8:00 PM'], focus: 'Food Competition & Family Day' }
      ];
    }
    
    // Default schedule
    const schedule = [];
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      schedule.push({
        date: d.toISOString().split('T')[0],
        times: [event.time],
        focus: `Day ${schedule.length + 1}`
      });
    }
    
    return schedule;
  }

  // Fetch all events with optional filtering
  async getEvents(filters = {}) {
    const cacheKey = `events_${JSON.stringify(filters)}`;
    
    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    try {
      const sql = `SELECT * FROM events_cache ORDER BY date ASC, time ASC`;
      const result = await this.query(sql);
      const events = result.results || [];

      // Transform database format to app format
      const transformedEvents = events.map(event => this.transformDatabaseEvent(event));

      // Cache the result
      this.cache.set(cacheKey, {
        data: transformedEvents,
        timestamp: Date.now(),
      });

      return transformedEvents;
    } catch (error) {
      console.error('Failed to fetch events:', error);
      // Return empty array if database fails
      return [];
    }
  }

  // Get distinct categories
  async getCategories() {
    try {
      const events = await this.getEvents();
      const categories = [...new Set(events.map(event => event.category))];
      return categories.sort();
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      return [];
    }
  }

  // Get distinct sources
  async getSources() {
    try {
      const events = await this.getEvents();
      const sources = [...new Set(events.map(event => event.source))];
      return sources.sort();
    } catch (error) {
      console.error('Failed to fetch sources:', error);
      return [];
    }
  }
}

// Export singleton instance
export const mcpDatabaseService = new MCPDatabaseService();
export default mcpDatabaseService;